// LearnCore Frontend - 学习集市场页面

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { Tag } from '../types';
import Loading from '../components/Loading';
import type { PublicStudySet, PublicReadingPassage, PublicClassicalPassage } from '../types';
import {
  BookOpenIcon,
  UserIcon,
  CalendarIcon,
  EyeIcon,
  PlayIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

type ContentType = 'sets' | 'passages' | 'classical';

export default function MarketplacePage() {
  const [contentType, setContentType] = useState<ContentType>('sets');
  const [publicSets, setPublicSets] = useState<PublicStudySet[]>([]);
  const [publicPassages, setPublicPassages] = useState<PublicReadingPassage[]>([]);
  const [publicClassicalPassages, setPublicClassicalPassages] = useState<PublicClassicalPassage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSets, setFilteredSets] = useState<PublicStudySet[]>([]);
  const [filteredPassages, setFilteredPassages] = useState<PublicReadingPassage[]>([]);
  const [filteredClassicalPassages, setFilteredClassicalPassages] = useState<PublicClassicalPassage[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [activeTagIds, setActiveTagIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (contentType === 'sets') {
      loadPublicSets();
    } else if (contentType === 'passages') {
      loadPublicPassages();
    } else if (contentType === 'classical') {
      loadPublicClassicalPassages();
    }
    loadAllTags();
  }, [contentType]);

  useEffect(() => {
    if (contentType === 'sets') {
      // 根据搜索查询过滤学习集
      if (searchQuery.trim() === '') {
        setFilteredSets(publicSets);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = publicSets.filter(set =>
          set.title.toLowerCase().includes(query) ||
          set.description?.toLowerCase().includes(query) ||
          set.author_email.toLowerCase().includes(query)
        );
        setFilteredSets(filtered);
      }
    } else if (contentType === 'passages') {
      // 根据搜索查询过滤阅读材料
      if (searchQuery.trim() === '') {
        setFilteredPassages(publicPassages);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = publicPassages.filter(passage =>
          passage.title.toLowerCase().includes(query) ||
          passage.original_text.toLowerCase().includes(query) ||
          passage.author_email.toLowerCase().includes(query)
        );
        setFilteredPassages(filtered);
      }
    } else if (contentType === 'classical') {
      // 根据搜索查询过滤文言文阅读材料
      if (searchQuery.trim() === '') {
        setFilteredClassicalPassages(publicClassicalPassages);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = publicClassicalPassages.filter(passage =>
          passage.title.toLowerCase().includes(query) ||
          passage.original_text.toLowerCase().includes(query) ||
          passage.author_email.toLowerCase().includes(query)
        );
        setFilteredClassicalPassages(filtered);
      }
    }
  }, [searchQuery, publicSets, publicPassages, publicClassicalPassages, contentType]);

  const loadPublicSets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getPublicStudySets({ tags: Array.from(activeTagIds) });
      // 解析标签数据
      const setsWithParsedTags = response.sets.map(set => ({
        ...set,
        tags: set.tags ? (typeof set.tags === 'string' ? JSON.parse(set.tags) : set.tags) : []
      }));
      setPublicSets(setsWithParsedTags);
    } catch (err: any) {
      setError(err.response?.data?.error || '获取公开学习集失败');
    } finally {
      setLoading(false);
    }
  };

  const loadPublicPassages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getPublicReadingPassages();
      setPublicPassages(response.passages);
    } catch (err: any) {
      setError(err.response?.data?.error || '获取公开阅读材料失败');
    } finally {
      setLoading(false);
    }
  };

  const loadPublicClassicalPassages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getPublicClassicalPassages();
      setPublicClassicalPassages(response.passages);
    } catch (err: any) {
      setError(err.response?.data?.error || '获取公开文言文材料失败');
    } finally {
      setLoading(false);
    }
  };

  const loadAllTags = async () => {
    try {
      const [grades, subjects] = await Promise.all([
        apiClient.getTags('grade'),
        apiClient.getTags('subject')
      ]);
      setAllTags([...(grades.tags||[]), ...(subjects.tags||[])]);
    } catch {}
  };

  const toggleActiveTag = (id: string) => {
    setActiveTagIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    // 标签变化时重新加载
    loadPublicSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTagIds]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载学习集市场..." />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center justify-center">
            <GlobeAltIcon className="h-8 w-8 mr-3 text-primary-600" />
            学习材料市场
          </h1>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-400 max-w-2xl mx-auto">
            发现其他用户分享的优质学习材料，加速你的学习进程。所有材料都是实时更新的，你看到的总是最新版本。
          </p>
        </div>

        {/* 内容类型切换 */}
        <div className="flex justify-center mb-6">
          <div className="bg-white dark:bg-gray-800 border border-gray- dark:border-gray-200 dark:border-gray- dark:border-gray-700 rounded-lg p-1">
            <div className="flex">
              <button
                onClick={() => setContentType('sets')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  contentType === 'sets'
                    ? 'bg-primary-100 text-primary-800'
                    : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <BookOpenIcon className="h-4 w-4 mr-2 inline" />
                卡片学习
              </button>
              <button
                onClick={() => setContentType('passages')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  contentType === 'passages'
                    ? 'bg-primary-100 text-primary-800'
                    : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <DocumentTextIcon className="h-4 w-4 mr-2 inline" />
                英语阅读
              </button>
              <button
                onClick={() => setContentType('classical')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  contentType === 'classical'
                    ? 'bg-primary-100 text-primary-800'
                    : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <AcademicCapIcon className="h-4 w-4 mr-2 inline" />
                文言阅读
              </button>
            </div>
          </div>
        </div>

        {/* 搜索栏 */}
        <div className="max-w-md mx-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="搜索学习集、描述或作者..."
              className="block w-full pl-10 pr-3 py-2 border border-gray- dark:border-gray-300 dark:border-gray- dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 标签筛选 */}
      <div className="mt-4 max-w-3xl mx-auto">
        <div className="flex flex-wrap gap-2 justify-center">
          {allTags.map(tag => (
            <button
              key={tag.id}
              type="button"
              onClick={() => toggleActiveTag(tag.id)}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${activeTagIds.has(tag.id) ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 border-primary-300 dark:border-primary-700' : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}`}
              title={tag.type}
            >
              {tag.name}
            </button>
          ))}
          {allTags.length === 0 && (
            <span className="text-xs text-gray-400">暂无标签</span>
          )}
        </div>
      </div>

      {/* 统计信息 */}
      <div className="mb-6 text-center">
        <div className="inline-flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg">
          <div className="flex items-center">
            {contentType === 'sets' ? (
              <>
                <BookOpenIcon className="h-4 w-4 mr-1" />
                <span>共 {publicSets.length} 个学习集</span>
              </>
            ) : contentType === 'passages' ? (
              <>
                <DocumentTextIcon className="h-4 w-4 mr-1" />
                <span>共 {publicPassages.length} 个阅读材料</span>
              </>
            ) : (
              <>
                <AcademicCapIcon className="h-4 w-4 mr-1" />
                <span>共 {publicClassicalPassages.length} 个文言材料</span>
              </>
            )}
          </div>
          {searchQuery && (
            <div className="flex items-center">
              <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
              <span>找到 {
                contentType === 'sets' ? filteredSets.length :
                contentType === 'passages' ? filteredPassages.length :
                filteredClassicalPassages.length
              } 个匹配结果</span>
            </div>
          )}
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                加载失败
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {error}
              </div>
              <div className="mt-4">
                <button
                  onClick={loadPublicSets}
                  className="btn-outline text-sm"
                >
                  重试
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 学习集列表 */}
      {contentType === 'sets' && (
        <>
          {filteredSets.length === 0 ? (
            <div className="text-center py-12">
              <GlobeAltIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery ? '没有找到匹配的学习集' : '还没有公开的学习集'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {searchQuery 
                  ? '尝试使用不同的搜索词，或者浏览所有可用的学习集。'
                  : '成为第一个分享学习集的用户！创建并分享你的学习集，帮助其他人学习。'
                }
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="btn-outline mr-4"
                >
                  清除搜索
                </button>
              )}
              <Link to="/sets/create" className="btn-primary">
                创建学习集
              </Link>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
              {filteredSets.map((set) => (
            <div 
              key={set.id} 
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray- dark:border-gray-200 dark:border-gray- dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col"
            >
              <div className="p-5 flex-1 flex flex-col">
                {/* 创建日期 + 作者信息 - 置顶 */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                  <span className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {formatDate(set.updated_at)}
                  </span>
                  
                  <div className="flex items-center">
                    {set.author_avatar_url ? (
                      <img src={set.author_avatar_url} alt="author" className="h-4 w-4 mr-1 rounded-full object-cover border" />
                    ) : (
                      <UserIcon className="h-4 w-4 mr-1" />
                    )}
                    <span className="truncate max-w-20">{set.author_nickname || set.author_email}</span>
                  </div>
                </div>

                {/* 标题 */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                  {set.title}
                </h3>

                {/* 描述 - 固定高度区域 */}
                <div className="mb-3 min-h-[2.5rem]">
                  {set.description ? (
                    <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                      {set.description}
                    </p>
                  ) : (
                    <div className="text-gray-400 text-sm italic">暂无描述</div>
                  )}
                </div>

                {/* 标签展示 - 固定高度区域 */}
                <div className="mb-4 min-h-[1.5rem]">
                  {Array.isArray(set.tags) && set.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {set.tags.map((t: any) => (
                        <span key={t.id} className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                          {t.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-400 text-xs italic">暂无标签</div>
                  )}
                </div>

                {/* 操作按钮 - 置底 */}
                <div className="flex space-x-2 mt-auto">
                  <Link
                    to={`/sets/${set.id}/study`}
                    className="flex-1 btn-primary text-center text-sm py-2"
                  >
                    <PlayIcon className="h-4 w-4 mr-1 inline" />
                    学习
                  </Link>
                  
                  <Link
                    to={`/sets/${set.id}/view`}
                    className="btn-outline px-3 py-2 text-sm"
                    title="查看详情"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Link>
                  
                  <div className="flex items-center text-xs text-gray-500 px-2">
                    <div className="flex items-center">
                      <BookOpenIcon className="h-3 w-3 mr-1" />
                      <span>{set.flashcard_count} 张卡片</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
          )}
        </>
      )}

      {/* 阅读材料列表 */}
      {contentType === 'passages' && (
        <>
          {filteredPassages.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery ? '没有找到匹配的阅读材料' : '还没有公开的阅读材料'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {searchQuery 
                  ? '尝试使用不同的搜索词，或者浏览所有可用的阅读材料。'
                  : '成为第一个分享阅读材料的用户！创建并分享你的阅读材料，帮助其他人学习。'
                }
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="btn-outline mr-4"
                >
                  清除搜索
                </button>
              )}
              <Link to="/passages/create" className="btn-primary">
                创建阅读材料
              </Link>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
              {filteredPassages.map((passage) => (
                <div 
                  key={passage.id} 
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray- dark:border-gray-200 dark:border-gray- dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col"
                >
                  <div className="p-5 flex-1 flex flex-col">
                    {/* 创建日期 + 作者信息 */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      <span className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        {formatDate(passage.updated_at)}
                      </span>
                      
                      <div className="flex items-center">
                        {passage.author_avatar_url ? (
                          <img src={passage.author_avatar_url} alt="author" className="h-4 w-4 mr-1 rounded-full object-cover border" />
                        ) : (
                          <UserIcon className="h-4 w-4 mr-1" />
                        )}
                        <span className="truncate max-w-20">{passage.author_nickname || passage.author_email}</span>
                      </div>
                    </div>

                    {/* 标题 */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                      {passage.title}
                    </h3>

                    {/* 原文预览 */}
                    <div className="mb-3 min-h-[2.5rem]">
                      <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                        {passage.original_text}
                      </p>
                    </div>

                    {/* 内容统计 */}
                    <div className="mb-4 flex flex-wrap gap-2 text-xs text-gray-500">
                      {passage.vocabulary_count > 0 && (
                        <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded">
                          {passage.vocabulary_count} 词汇
                        </span>
                      )}
                      {passage.grammar_point_count > 0 && (
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                          {passage.grammar_point_count} 语法
                        </span>
                      )}
                      {passage.collocation_count > 0 && (
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded">
                          {passage.collocation_count} 搭配
                        </span>
                      )}
                      {passage.complex_sentence_count > 0 && (
                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded">
                          {passage.complex_sentence_count} 长难句
                        </span>
                      )}
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex space-x-2 mt-auto">
                      <Link
                        to={`/passages/${passage.id}/study`}
                        className="flex-1 btn-primary text-center text-sm py-2"
                      >
                        <PlayIcon className="h-4 w-4 mr-1 inline" />
                        学习
                      </Link>
                      
                      <Link
                        to={`/passages/${passage.id}/view`}
                        className="btn-outline px-3 py-2 text-sm"
                        title="查看详情"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* 文言文阅读材料列表 */}
      {contentType === 'classical' && (
        <>
          {filteredClassicalPassages.length === 0 ? (
            <div className="text-center py-12">
              <AcademicCapIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery ? '没有找到匹配的文言材料' : '还没有公开的文言材料'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {searchQuery
                  ? '尝试使用不同的搜索词，或者浏览所有可用的文言材料。'
                  : '成为第一个分享文言材料的用户！创建并分享你的文言材料，帮助其他人学习。'
                }
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="btn-outline mr-4"
                >
                  清除搜索
                </button>
              )}
              <Link to="/classical/create" className="btn-primary">
                创建文言材料
              </Link>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
              {filteredClassicalPassages.map((passage) => (
                <div
                  key={passage.id}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray- dark:border-gray-200 dark:border-gray- dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col"
                >
                  <div className="p-5 flex-1 flex flex-col">
                    {/* 标题和作者 */}
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 flex-1">
                        {passage.title}
                      </h3>
                    </div>

                    {/* 作者和出处 */}
                    {((passage as any).author || (passage as any).source) && (
                      <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                        {(passage as any).author && <span>作者：{(passage as any).author}</span>}
                        {(passage as any).author && (passage as any).source && <span className="mx-2">·</span>}
                        {(passage as any).source && <span>出处：{(passage as any).source}</span>}
                      </div>
                    )}

                    {/* 内容预览 */}
                    <div className="mb-4 min-h-[3rem]">
                      <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3 font-serif">
                        {passage.original_text}
                      </p>
                    </div>

                    {/* 内容统计 */}
                    <div className="mb-4 flex flex-wrap gap-2 text-xs text-gray-500">
                      {(passage as any).key_term_count > 0 && (
                        <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded">
                          {(passage as any).key_term_count} 关键词
                        </span>
                      )}
                      {passage.translation_count > 0 && (
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                          {passage.translation_count} 译文
                        </span>
                      )}
                      {(passage as any).analysis_count > 0 && (
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded">
                          {(passage as any).analysis_count} 解析
                        </span>
                      )}
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex space-x-2 mt-auto">
                      <Link
                        to={`/classical/${passage.id}/study`}
                        className="flex-1 btn-primary text-center text-sm py-2"
                      >
                        <PlayIcon className="h-4 w-4 mr-1 inline" />
                        学习
                      </Link>

                      <Link
                        to={`/classical/${passage.id}/view`}
                        className="btn-outline px-3 py-2 text-sm"
                        title="查看详情"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* 底部提示 */}
      {((contentType === 'sets' && filteredSets.length > 0) ||
        (contentType === 'passages' && filteredPassages.length > 0) ||
        (contentType === 'classical' && filteredClassicalPassages.length > 0)) && (
        <div className="mt-12 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
            <h3 className="text-sm font-medium text-blue-900 mb-1">
              💡 提示
            </h3>
            <p className="text-sm text-blue-700">
              {contentType === 'sets'
                ? '这些学习集由社区用户创建和分享。如果学习集作者进行了更新，你总是能看到最新的内容。喜欢某个学习集？不妨给作者发个感谢邮件！'
                : contentType === 'passages'
                ? '这些阅读材料由社区用户创建和分享。如果材料作者进行了更新，你总是能看到最新的内容。喜欢某个阅读材料？不妨给作者发个感谢邮件！'
                : '这些文言材料由社区用户创建和分享。如果材料作者进行了更新，你总是能看到最新的内容。喜欢某个文言材料？不妨给作者发个感谢邮件！'
              }
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
